import { ReactNode, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FilterBar,
  FilterBarProps,
} from "~/components/Filter/FilterBar/FilterBar";
import {
  FilterForm,
  FilterFormProps,
} from "~/components/Filter/FilterForm/FilterForm";
import { QuickFilterBar } from "~/components/Filter/QuickFilterBar/QuickFilterBar";
import {
  QuickFilterButton,
  QuickFilterButtonProps,
} from "~/components/Filter/QuickFilterButton/QuickFilterButton";
import { ActiveFilterBar } from "~/components/Filter/ActiveFilterBar/ActiveFilterBar";
import {
  ActiveFilterButton,
  ActiveFilterButtonProps,
} from "~/components/Filter/ActiveFilterButton/ActiveFilterButton";
import { FilterMenuOption } from "~/components/Filter/FilterMenu";
import { GROUPED_MEMBERS_ROLES } from "~/components/Members/const";
import { addFilter, removeFilter } from "~/pages/integrations/workflow/utils";

export type UsersFilterVariables = {
  rolesFilter?: string[];
  query?: string;
};

export type UseUsersFilterContextOutput = {
  filterVariables: UsersFilterVariables;
  filterForm: ReactNode;
  activeFilterCount: number;
};

export function useUsersFilterContext(): UseUsersFilterContextOutput {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // search text typed into filter bar
  const [searchInput, setSearchInput] = useState("");
  const [focusedField, setFocusedField] = useState<FilterMenuOption>();

  // Get role filters from URL params
  const roleFilterParams = searchParams.getAll("role");
  const searchFilterParams = searchParams.getAll("queryTerm");

  const roleFilters = roleFilterParams.length ? roleFilterParams : undefined;
  const searchFilters = searchFilterParams.length
    ? searchFilterParams
    : undefined;

  // Build filter variables for GraphQL query
  const filterVariables: UsersFilterVariables = useMemo(() => {
    return {
      ...(roleFilters?.length ? { rolesFilter: roleFilters } : {}),
      // Combine all search terms into a single query string
      ...(searchFilters?.length ? { query: searchFilters.join(" ") } : {}),
    };
  }, [JSON.stringify(roleFilterParams), JSON.stringify(searchFilterParams)]);

  // Convert grouped member roles to menu options
  const roleMenuOptions: FilterMenuOption[] = GROUPED_MEMBERS_ROLES.flatMap(
    (group) =>
      group.options.map((role) => ({
        param: "role",
        field: `role.${role.value}`,
        value: role.value, // MRN like "//iam.api.mondoo.app/roles/owner"
        label: role.label, // Display name like "Owner"
        group: group.groupLabel, // "Base Roles" or "Detailed Permissions"
        isMultiple: true,
        onSelect: () => addFilter(navigate, "role", role.value, true),
      })),
  );

  // Get field options based on focused field
  const fieldOptions = useUsersSuggestions(
    focusedField?.field || "",
    searchInput,
    roleMenuOptions,
  );

  // Build active filters list
  const activeFilters: ActiveFilterButtonProps[] = [
    roleFilters
      ? roleFilters.map((roleFilter) => {
          // Find the role label from GROUPED_MEMBERS_ROLES
          const roleLabel =
            GROUPED_MEMBERS_ROLES.flatMap((group) => group.options).find(
              (role) => role.value === roleFilter,
            )?.label || roleFilter;

          return {
            label: "Role",
            value: roleLabel,
            onClick: () => removeFilter(navigate, "role", roleFilter),
          };
        })
      : [],
    searchFilters
      ? searchFilters.map((searchFilter) => {
          return {
            label: "Search",
            value: searchFilter,
            onClick: () => removeFilter(navigate, "queryTerm", searchFilter),
          };
        })
      : [],
  ].flatMap((f) => f);

  // Calculate active filter count
  const activeFilterCount = activeFilters.length;

  // Reset all filters
  const handleResetFilters = () => {
    const url = new URL(location.href);
    url.searchParams.delete("role");
    url.searchParams.delete("queryTerm");
    navigate(url.href.replace(url.origin, ""));
  };

  // Render filter bar
  const filterBar = renderFilterBar({
    fieldOptions: fieldOptions.suggestions,
    focusedField,
    inputValue: searchInput,
    onInputValueChange: (value: string) => {
      setSearchInput(value);
    },
    onFocusedRemove: () => {
      setFocusedField(undefined);
    },
    onFilterSelect: (filter) => {
      setSearchInput("");
      if (!focusedField) {
        setFocusedField(filter);
      } else {
        setFocusedField(undefined);
        if (filter.param) {
          addFilter(navigate, filter.param, filter.value, filter.isMultiple);
        }
      }
    },
    onFilterCreate: (filter) => {
      setFocusedField(undefined);
      setSearchInput("");
      addFilter(navigate, "queryTerm", filter, true);
    },
  });

  // Render quick filter bar
  const quickFilterBar = renderQuickFilterBar([
    {
      label: "Role",
      activeCount: roleFilters?.length || 0,
      menuOptions: roleMenuOptions,
    },
  ]);

  // Render active filter bar
  const activeFilterBar = renderActiveFilterBar(
    activeFilters,
    handleResetFilters,
  );

  // Render filter form
  const filterForm = renderFilterForm({
    filterBar,
    quickFilterBar,
    activeFilterBar,
  });

  return {
    filterVariables,
    filterForm,
    activeFilterCount,
  };
}

function useUsersSuggestions(
  field: string,
  searchInput: string,
  roleMenuOptions: FilterMenuOption[],
) {
  // Category suggestions (when no field is focused)
  const categorySuggestions = useMemo(() => {
    const roleFilterGroup: FilterMenuOption = {
      field: "role",
      label: "Role",
      value: "role",
    };

    const queryFilterGroup: FilterMenuOption = {
      field: "queryTerm",
      label: `Press enter to free text search "${searchInput}"`,
      value: "queryTerm",
      isDisabled: true,
    };

    const suggestions = [
      searchInput.length > 0 ? queryFilterGroup : undefined,
      roleFilterGroup,
    ]
      .flatMap((g) => g ?? [])
      .filter((s) =>
        s.label.toLocaleLowerCase().includes(searchInput.toLocaleLowerCase()),
      );

    return {
      suggestions,
      loading: false,
      error: false,
    };
  }, [searchInput]);

  // Return appropriate suggestions based on focused field
  if (field === "role") {
    return {
      suggestions: roleMenuOptions,
      loading: false,
      error: false,
    };
  }

  return categorySuggestions;
}

function renderFilterBar(props: FilterBarProps) {
  return (
    <FilterBar
      {...props}
      sx={{ backgroundColor: "background.paper", borderRadius: 2 }}
    />
  );
}

function renderQuickFilterBar(quickFilters: QuickFilterButtonProps[]) {
  return (
    <QuickFilterBar mt={0.5}>
      {quickFilters.map((quickFilterProps) => {
        const key = quickFilterProps.label;
        return (
          <QuickFilterButton
            key={key}
            {...quickFilterProps}
            sx={{ px: 1, py: 0.5, boxShadow: "none" }}
          />
        );
      })}
    </QuickFilterBar>
  );
}

function renderActiveFilterBar(
  activeFilters: ActiveFilterButtonProps[],
  onResetClick: () => void,
) {
  return (
    activeFilters.length > 0 && (
      <ActiveFilterBar onResetClick={onResetClick}>
        {activeFilters.map((props) => {
          const key = props.label + props.value;
          return <ActiveFilterButton key={key} {...props} />;
        })}
      </ActiveFilterBar>
    )
  );
}

function renderFilterForm(props: FilterFormProps) {
  return <FilterForm {...props} />;
}
